import {
  IconCreditCard,
  useAppContext,
} from "@ftdr/blueprint-components-react";
import React, { useMemo } from "react";
import classNames from "classnames";
import { BankIcon, PayPalLogo, PaypalNewIcon } from "../assets/icons";
import {
  AvailablePaymentMethods,
  defaultAvailablePaymentMethods,
  PaymentMethods,
  PAYMENTS_METHODS,
} from "../const";
import { usePaymentContext } from "../contexts/payment-context";
import { generateAvailablePaymentMethodsArray } from "../utils";
import { PaymentTypeItem } from "./payment-type-item";
import { PaymentComponentState } from "./payments-component";
import { IconWrapper } from "./icon-wrapper";
import { WalletCheckbox } from "./wallet-checkbox";
import {
  setIsSetAsDefaultChecked,
  setIsSaveInWalletChecked,
} from "../contexts/payment-context/state";
import { WalletCheckboxValue } from "../types";

export type PaymentsUIProps = {
  // ------------------------- UI Related Value Props -------------------------

  /**
   * Use this prop to apply custom CSS styling to the root element of the MFE.
   */
  className?: string;
  paymentMethodContainerClassName?: string;
  /**
   * Payments children props
   */
  cardProcessor: JSX.Element;
  bankProcessor: JSX.Element;
  paypalProcessor: JSX.Element;
  achPreloadElement?: JSX.Element;

  cardProcessorIcon?: JSX.Element;
  bankProcessorIcon?: JSX.Element;
  paypalProcessorIcon?: JSX.Element;
  // ----------------------- UI Related Event Props -----------------------
  internalState?: PaymentComponentState;

  handlePaymentTypeChange: (activePaymentMethod: PaymentMethods) => void;
  availablePaymentMethods?: AvailablePaymentMethods;
};
export const PaymentsUI: React.FC<PaymentsUIProps> = ({
  className,
  paymentMethodContainerClassName,
  cardProcessor,
  bankProcessor,
  paypalProcessor,
  achPreloadElement,
  cardProcessorIcon,
  bankProcessorIcon,
  paypalProcessorIcon,
  internalState,
  handlePaymentTypeChange,
}) => {
  const {
    appSettings: { localizedText },
  } = useAppContext();

  const {
    availablePaymentMethods,
    dispatch,
    styleType,
    setWalletCheckboxProps,
    state: {
      isSetAsDefaultChecked,
      isSaveInWalletChecked,
      isPending,
      isFirstRegistrationPending,
      showRadio,
    },
  } = usePaymentContext();

  const isDefault = useMemo(() => {
    return styleType === "default";
  }, [styleType]);

  const renderItems = useMemo(
    () => ({
      CREDIT_CARD: {
        paymentProcessor: cardProcessor,
        icon: cardProcessorIcon || (
          <IconCreditCard
            data-testid={`Card_Processor_Icon`}
            size={isDefault ? "32" : "24"}
            color={isDefault ? undefined : "primary"}
          />
        ),
      },
      BANK_ACCOUNT: {
        paymentProcessor: bankProcessor,
        preload: achPreloadElement,
        icon: bankProcessorIcon || (
          <IconWrapper Icon={BankIcon} styleType={styleType} />
        ),
      },
      PAYPAL: {
        paymentProcessor: paypalProcessor,
        icon: paypalProcessorIcon || (
          <IconWrapper
            Icon={styleType === "HBW210" ? PaypalNewIcon : PayPalLogo}
            styleType={styleType}
          />
        ),
      },
    }),
    [
      cardProcessor,
      cardProcessorIcon,
      bankProcessor,
      achPreloadElement,
      bankProcessorIcon,
      paypalProcessor,
      paypalProcessorIcon,
      isDefault,
      styleType,
    ]
  );

  const availablePaymentMethodsArray = useMemo(() => {
    try {
      return generateAvailablePaymentMethodsArray(
        availablePaymentMethods,
        renderItems
      );
    } catch (e) {
      console.error("Wrong availablePaymentMethods property is passed");
      return generateAvailablePaymentMethodsArray(
        defaultAvailablePaymentMethods,
        renderItems
      );
    }
  }, [availablePaymentMethods, renderItems]);

  return (
    <div className={`block ${className}`}>
      <div
        data-styleid="PaymentsUIContainer"
        className={classNames(
          `PaymentsUIContainer flex flex-col ${paymentMethodContainerClassName}`
        )}
      >
        {availablePaymentMethodsArray.map((paymentMethod, index) => (
          <PaymentTypeItem
            key={index}
            activePaymentMethod={internalState?.activePaymentMethod}
            paymentTypeLabel={localizedText(
              `PAYMENT_TYPE_LABEL_${paymentMethod.name}`
            )}
            icon={paymentMethod.icon}
            paymentType={PAYMENTS_METHODS[paymentMethod.name]}
            handleOnChange={handlePaymentTypeChange}
            preloadRenderItem={paymentMethod.preload}
            paymentTypeRenderItem={paymentMethod.paymentProcessor}
            data-testid={`Payment__Type__${paymentMethod}`}
            showRadio={showRadio}
          />
        ))}
      </div>
      {setWalletCheckboxProps?.showWalletCheckbox && (
        <div className={"mt-4 ml-2"}>
          <WalletCheckbox
            label={setWalletCheckboxProps?.label}
            isDisabled={isPending || isFirstRegistrationPending}
            isChecked={
              setWalletCheckboxProps?.showWalletCheckbox ===
              WalletCheckboxValue.SET_AS_DEFAULT
                ? !!isSetAsDefaultChecked
                : !!isSaveInWalletChecked
            }
            onCheckedChange={() => {
              dispatch(
                setWalletCheckboxProps?.showWalletCheckbox ===
                  WalletCheckboxValue.SET_AS_DEFAULT
                  ? setIsSetAsDefaultChecked(!isSetAsDefaultChecked)
                  : setIsSaveInWalletChecked(!isSaveInWalletChecked)
              );
            }}
            showWalletCheckbox={setWalletCheckboxProps.showWalletCheckbox}
          />
        </div>
      )}
    </div>
  );
};
